import { useQuery, useQueryClient } from "@tanstack/react-query";
import { History, UserX } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";

export const PastRoommates = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["past-members"], queryFn: async () => (await api.get("/household/members/past")).data });
  const past = data?.members || [];
  if (!past.length) return null;

  const purge = async (m) => {
    try {
      const { data: res } = await api.delete(`/household/members/${m.id}/purge`);
      ["past-members", "members", "dashboard", "expenses", "history"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(`${m.name || m.email} removed completely${res.expenses_removed ? ` (${res.expenses_removed} expense(s) deleted)` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  return (
    <div className="rounded-3xl border border-border bg-card p-5" data-testid="past-roommates-card">
      <div className="flex items-center gap-2 mb-1"><History className="h-4 w-4 text-primary" /><h2 className="font-heading font-bold">Past roommates ({past.length})</h2></div>
      <p className="text-xs text-muted-foreground mb-3">Removed roommates still count toward the months they lived here. Remove them completely if they were added by mistake or were a test account.</p>
      <div className="space-y-2">
        {past.map((m) => (
          <div key={m.id} className="flex items-center gap-3 rounded-xl bg-secondary/50 p-3" data-testid="past-member-row">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{m.name || m.email}</div>
              <div className="text-xs text-muted-foreground truncate">{m.email}</div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="sm" variant="ghost" className="text-rose-600 shrink-0" data-testid="purge-member-btn"><UserX className="h-4 w-4 mr-1" /> Remove completely</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove {m.name || m.email} completely?</AlertDialogTitle>
                  <AlertDialogDescription>They will be taken out of every month's split, and any expenses they added will be deleted. Everyone's balances will be recalculated. This cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => purge(m)} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-purge-btn">Remove completely</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ))}
      </div>
    </div>
  );
};
